<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from '@lucide/vue'
import { calendarDays } from './dateValue'
import { useUiConfig } from '../config-provider/context'

const props = withDefaults(defineProps<{
  viewDate: Dayjs
  value?: Dayjs | null
  picker?: 'date' | 'month' | 'year'
  range?: [Dayjs | null, Dayjs | null]
  hoverValue?: Dayjs | null
  disabledDate?: (date: Dayjs) => boolean
  showPrev?: boolean
  showNext?: boolean
}>(), { picker: 'date', showPrev: true, showNext: true })
const emit = defineEmits<{ 'update:viewDate': [value: Dayjs]; select: [value: Dayjs]; hover: [value: Dayjs]; panelChange: [value: Dayjs, mode: 'date' | 'month' | 'year'] }>()
const config = useUiConfig()
const locale = computed(() => config.value.locale?.locale?.replace('_', '-').toLowerCase() ?? 'zh-cn')
const mode = ref(props.picker)
const panelRef = ref<HTMLElement | null>(null)
const focusDate = ref((props.value ?? props.viewDate).format('YYYY-MM-DD'))
const days = computed(() => calendarDays(props.viewDate, locale.value))
const weekdays = computed(() => {
  const start = props.viewDate.locale(locale.value).startOf('week')
  return Array.from({ length: 7 }, (_, index) => start.add(index, 'day').format('dd'))
})
const years = computed(() => { const first = Math.floor(props.viewDate.year() / 10) * 10; return Array.from({ length: 12 }, (_, index) => first - 1 + index) })
const today = computed(() => dayjs().format('YYYY-MM-DD'))
watch(() => props.picker, value => { mode.value = value })
watch(() => props.viewDate, date => { if (!days.value.some(day => day.format('YYYY-MM-DD') === focusDate.value)) focusDate.value = date.startOf('month').format('YYYY-MM-DD') })
function navigate(amount: number, large = false) {
  const unit = mode.value === 'date' && !large ? 'month' : 'year'
  const count = mode.value === 'year' ? amount * 10 : amount
  emit('update:viewDate', props.viewDate.add(count, unit))
}
function changeMode(value: 'date' | 'month' | 'year') { mode.value = value; emit('panelChange', props.viewDate, value) }
function chooseMonth(month: number) {
  const date = props.viewDate.date(1).month(month)
  emit('update:viewDate', date)
  if (props.picker === 'month') { if (!props.disabledDate?.(date)) emit('select', date) }
  else changeMode('date')
}
function chooseYear(year: number) {
  const date = props.viewDate.date(1).year(year)
  emit('update:viewDate', date)
  if (props.picker === 'year') { if (!props.disabledDate?.(date)) emit('select', date) }
  else changeMode('month')
}
function rangeState(date: Dayjs) {
  const [start, savedEnd] = props.range ?? [null, null]
  const end = savedEnd ?? props.hoverValue
  if (!start) return {}
  const first = end && end.isBefore(start, 'day') ? end : start
  const last = end && end.isBefore(start, 'day') ? start : end
  return { 'is-range-start': date.isSame(first, 'day'), 'is-range-end': Boolean(last && date.isSame(last, 'day')), 'is-in-range': Boolean(last && date.isAfter(first, 'day') && date.isBefore(last, 'day')) }
}
function select(date: Dayjs) { if (!props.disabledDate?.(date)) emit('select', date) }
async function keydown(event: KeyboardEvent, date: Dayjs) {
  let next: Dayjs
  if (event.key === 'ArrowLeft') next = date.subtract(1, 'day')
  else if (event.key === 'ArrowRight') next = date.add(1, 'day')
  else if (event.key === 'ArrowUp') next = date.subtract(7, 'day')
  else if (event.key === 'ArrowDown') next = date.add(7, 'day')
  else if (event.key === 'PageUp') next = date.subtract(1, 'month')
  else if (event.key === 'PageDown') next = date.add(1, 'month')
  else if (event.key === 'Home') next = date.locale(locale.value).startOf('week')
  else if (event.key === 'End') next = date.locale(locale.value).endOf('week')
  else return
  event.preventDefault()
  focusDate.value = next.format('YYYY-MM-DD')
  if (!next.isSame(props.viewDate, 'month')) emit('update:viewDate', next.startOf('month'))
  await nextTick()
  panelRef.value?.querySelector<HTMLElement>(`[data-date="${focusDate.value}"]`)?.focus()
}
defineExpose({ focus: () => panelRef.value?.querySelector<HTMLElement>('[tabindex="0"]')?.focus() })
</script>

<template>
  <section ref="panelRef" class="rr-calendar" :aria-label="viewDate.format('YYYY-MM')">
    <header class="rr-calendar-header">
      <div class="rr-calendar-nav"><button v-if="showPrev" type="button" aria-label="上一年" @click="navigate(-1, true)"><ChevronsLeft :size="14" /></button><button v-if="showPrev && mode === 'date'" type="button" aria-label="上一月" @click="navigate(-1)"><ChevronLeft :size="14" /></button></div>
      <div class="rr-calendar-heading"><button type="button" @click="changeMode('year')">{{ mode === 'year' ? `${years[1]}–${years[10]}` : viewDate.format('YYYY') }}</button><button v-if="mode === 'date'" type="button" @click="changeMode('month')">{{ viewDate.locale(locale).format('MMM') }}</button></div>
      <div class="rr-calendar-nav"><button v-if="showNext && mode === 'date'" type="button" aria-label="下一月" @click="navigate(1)"><ChevronRight :size="14" /></button><button v-if="showNext" type="button" aria-label="下一年" @click="navigate(1, true)"><ChevronsRight :size="14" /></button></div>
    </header>
    <div v-if="mode === 'date'" class="rr-calendar-grid" role="grid" :aria-label="viewDate.format('YYYY-MM')">
      <div class="rr-calendar-weekdays" role="row"><span v-for="weekday in weekdays" :key="weekday" role="columnheader">{{ weekday }}</span></div>
      <div v-for="week in 6" :key="week" class="rr-calendar-week" role="row">
        <div v-for="date in days.slice((week - 1) * 7, week * 7)" :key="date.format('YYYY-MM-DD')" role="gridcell" :aria-selected="Boolean(value?.isSame(date, 'day'))" :class="rangeState(date)">
          <button type="button" class="rr-calendar-day" :data-date="date.format('YYYY-MM-DD')" :tabindex="focusDate === date.format('YYYY-MM-DD') ? 0 : -1" :aria-disabled="disabledDate?.(date) || undefined" :aria-label="date.format('YYYY-MM-DD')" :class="{ 'is-other-month': !date.isSame(viewDate, 'month'), 'is-today': date.format('YYYY-MM-DD') === today, 'is-selected': value?.isSame(date, 'day'), 'is-disabled': disabledDate?.(date) }" @focus="focusDate = date.format('YYYY-MM-DD')" @keydown="keydown($event, date)" @click="select(date)" @mouseenter="emit('hover', date)"><slot name="dateRender" :current="date" :today="dayjs()">{{ date.date() }}</slot></button>
        </div>
      </div>
    </div>
    <div v-else-if="mode === 'month'" class="rr-calendar-choices"><button v-for="month in 12" :key="month" type="button" :class="{ 'is-selected': value?.year() === viewDate.year() && value?.month() === month - 1 }" @click="chooseMonth(month - 1)">{{ viewDate.month(month - 1).locale(locale).format('MMM') }}</button></div>
    <div v-else class="rr-calendar-choices"><button v-for="year in years" :key="year" type="button" :class="{ 'is-selected': value?.year() === year }" @click="chooseYear(year)">{{ year }}</button></div>
  </section>
</template>
